import { useState, useEffect, useCallback } from "react";
import { supabase } from "../supabase";

// ─── Résolution du tenant depuis le sous-domaine ───
// deltasun.bativiz.fr → "deltasun"
// admin.bativiz.fr   → "admin"
// localhost           → fallback env var REACT_APP_TENANT_SCHEMA
function getSubdomain() {
  const hostname = window.location.hostname;

  // Dev local : pas de sous-domaine, fallback sur la variable d'env
  if (hostname === "localhost" || hostname === "127.0.0.1") {
    return process.env.REACT_APP_TENANT_SCHEMA || "deltasun";
  }

  // Production : extraire le sous-domaine (premier segment avant le premier point)
  const parts = hostname.split(".");
  if (parts.length >= 2) return parts[0];

  return "deltasun"; // fallback sécurité
}

/**
 * Hook principal d'authentification Bati Viz
 *
 * Gère tout le flux auth :
 * 1. Récupère la session Supabase Auth
 * 2. Résout le sous-domaine (quel client ?)
 * 3. Charge le profil utilisateur + infos tenant via RPC get_my_profile
 * 4. Si admin sur un sous-domaine client → charge le tenant du sous-domaine
 * 5. Vérifie que le tenant est actif
 * 6. Vérifie que le user a le droit d'accéder à ce sous-domaine
 *
 * Retourne : { session, profile, tenant, tenantId, role, loading, isReset, isAdmin, subdomain, error, signOut }
 */
export function useAuth() {

  const [session, setSession] = useState(null);
  const [profile, setProfile] = useState(null);
  const [tenant, setTenant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isReset, setIsReset] = useState(false);
  const [error, setError] = useState(null);

  const subdomain = getSubdomain();
  const isAdmin = subdomain === "admin";

  // ─── Déconnexion ───
  const signOut = useCallback(async () => {
    await supabase.auth.signOut();
    setSession(null);
    setProfile(null);
    setTenant(null);
  }, []);

  // ─── Initialisation : session + écoute changements auth ───
  useEffect(() => {
    // Détecte si on arrive via un lien de reset mot de passe
    if (window.location.pathname === "/reset-password") {
      setIsReset(true);
    }

    // Récupère la session active (cookie/token existant)
      supabase.auth.getSession().then(({ data: { session: s } }) => {
      setSession(s);
      if (!s) setLoading(false);
    });

    // Écoute les événements auth (login, logout, reset password)
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, s) => {
      setSession(s);
      if (event === "PASSWORD_RECOVERY") setIsReset(true);
      if (!s) {
        setProfile(null);
        setTenant(null);
        setLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  // ─── Quand la session change : charger profil + tenant ───
  // En mode reset password, on ne charge pas le profil (pas besoin de tenant)
  useEffect(() => {
    if (!session) return;

    async function loadProfile() {
      try {
        // Appel RPC get_my_profile — retourne profil + infos tenant en une seule requête
        const { data, error: rpcError } = await supabase.rpc("get_my_profile");

        if (rpcError) {
          console.error("Erreur get_my_profile:", rpcError);
          setError("Impossible de charger le profil");
          setLoading(false);
          return;
        }

        // get_my_profile retourne un tableau (RETURNS TABLE), on prend la première ligne
        const row = Array.isArray(data) ? data[0] : data;

        if (!row) {
          console.error("Aucun profil trouvé pour cet utilisateur");
          setError("Profil non configuré. Contactez l'administrateur.");
          setLoading(false);
          return;
        }

        // Construire l'objet profil
        const prof = {
          id: row.user_id,
          email: row.email,
          nom: row.nom,
          prenom: row.prenom,
          role: row.role,
          actif: row.actif,
          tenantId: row.tenant_id,
        };

        // Construire l'objet tenant depuis le profil
        const profTenant = {
          id: row.tenant_id,
          code: row.tenant_code,
          nom: row.tenant_nom,
          schemaName: row.schema_name,
          maxUsers: row.max_users,
          extraUsers: row.extra_users,
          actif: row.tenant_actif !== undefined ? row.tenant_actif : true,
          syncLastRun: row.sync_last_run || null,
        };

        setProfile(prof);

        // Variable locale pour suivre le tenant résolu
        // (setTenant est asynchrone, on ne peut pas lire le state juste après)
        let resolvedTenant = profTenant;

        // ─── Résolution du tenant pour les admins ───
        // Si admin sur un sous-domaine client (ex: dupont.bativiz.fr)
        // → charger le tenant du sous-domaine, pas celui du profil admin
        if (prof.role === "admin" && !isAdmin && profTenant.schemaName !== subdomain) {
          const { data: subTenant, error: subError } = await supabase
            .from("tenants")
            .select("*")
            .eq("schema_name", subdomain)
            .single();

          if (subError || !subTenant) {
            setError("Client introuvable pour ce sous-domaine.");
            setLoading(false);
            return;
          }

          resolvedTenant = {
            id: subTenant.id,
            code: subTenant.code,
            nom: subTenant.nom,
            schemaName: subTenant.schema_name,
            maxUsers: subTenant.max_users,
            extraUsers: subTenant.extra_users,
            actif: subTenant.actif,
            syncLastRun: subTenant.sync_last_run || null,
          };
        }

        // Appliquer le tenant résolu au state
        setTenant(resolvedTenant);

        // Vérification : le user non-admin a-t-il accès à ce sous-domaine ?
        if (!isAdmin && prof.role !== "admin" && profTenant.schemaName !== subdomain) {
          console.warn(
            `Accès refusé : user tenant=${profTenant.schemaName}, subdomain=${subdomain}`
          );
          setError("Vous n'avez pas accès à cette plateforme.");
          await signOut();
          return;
        }

        // Vérification : tenant actif ? (sauf pour les admins)
        if (prof.role !== "admin" && !resolvedTenant.actif) {
          setError("Cette plateforme est désactivée. Contactez l'administrateur.");
          setLoading(false);
          return;
        }

        // Vérification : compte actif ?
        if (!prof.actif) {
          setError("Votre compte est désactivé. Contactez l'administrateur.");
          await signOut();
          return;
        }

        setError(null);
      } catch (err) {
        console.error("Erreur chargement profil:", err);
        setError("Erreur de chargement");
      } finally {
        setLoading(false);
      }
    }

    loadProfile();
  }, [session, subdomain, isAdmin, signOut]);
  
  return {
    session,
    profile,
    tenant,
    tenantId: tenant?.id || null,
    role: profile?.role || null,
    loading,
    isReset,
    isAdmin,
    subdomain,
    error,
    signOut,
  };
}